/**
 * Kiosk command poll — the delivery leg of Plan 23's force-update flow.
 *
 * Plan 23 assumed a live WebSocket/SSE channel that was never built
 * (deferred.md marks it v2), so command delivery is poll-shaped like
 * everything else in the kiosk domain: while this tab is a paired kiosk
 * (`profile.kioskContext.paired`), drain
 * `POST /api/kiosk-sessions/:id/commands/claim` on an interval and act
 * on whatever comes back. Today the only command is FORCE_UPDATE, which
 * hands off to `requestForceUpdate()` (sw/register.ts): the SW updates,
 * skips waiting, and reloads every client behind the "Updating Ordo"
 * overlay.
 *
 * Poll-shaped delivery also provides the plan's staggered bulk reloads
 * for free — each kiosk drains on its own poll offset, so a fleet never
 * reloads in unison.
 */
import { useEffect } from 'react'

import { claimKioskCommands } from '@/api/kiosk-sessions'
import { useProfile } from '@/stores/profile'
import { requestForceUpdate } from '@/sw/register'

export const KIOSK_COMMAND_POLL_INTERVAL_MS = 15_000

export function useKioskCommandPoller(): void {
  const { profile } = useProfile()
  const kioskContext = profile?.kioskContext
  const sessionId = kioskContext?.paired ? kioskContext.sessionId : null

  useEffect(() => {
    if (!sessionId) {
      return
    }
    let cancelled = false
    let inFlight = false

    async function poll() {
      if (cancelled || inFlight || !sessionId) {
        return
      }
      inFlight = true
      try {
        const commands = await claimKioskCommands(sessionId)
        if (!cancelled && commands.forceUpdate) {
          await requestForceUpdate()
        }
      } catch {
        // Transient network errors are routine on a kiosk (Wi-Fi blips,
        // service-time congestion) — the next tick retries, and an
        // unclaimed command waits server-side until then.
      } finally {
        inFlight = false
      }
    }

    const timer = window.setInterval(() => void poll(), KIOSK_COMMAND_POLL_INTERVAL_MS)
    void poll()
    return () => {
      cancelled = true
      window.clearInterval(timer)
    }
  }, [sessionId])
}
